import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave, faClock, faFlagCheckered, faLink, faUnlink, faUser, faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';
import SearchableMultiSelect from '../../components/SearchableMultiSelect';
import ConfirmModal from './components/ConfirmModal';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import VagueBadge from './components/VagueBadge';
import PaymentRelanceCard from './components/PaymentRelanceCard';

const STEP_LABELS = { setup: 'Setup', terrain: 'Terrain', reporting: 'Reporting', closing: 'Closing' };
const STEP_COLORS = { setup: '#f59e0b', terrain: '#10b981', reporting: '#0ea5e9', closing: '#8b5cf6' };
const PAYMENT_LABELS = { per_complete: 'Par complété', per_day: 'Par jour' };

// Normalize an API date (ISO string or Y-m-d) to YYYY-MM-DD for <input type="date">
// Uses LOCAL date parts so a timezone-shifted ISO timestamp still maps to the visual day.
const toDateInput = (v) => {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (isNaN(d)) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const SlotEdit = () => {
  const { id: projectId, slotId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [waves, setWaves] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [form, setForm] = useState({ name: '', wave_id: null, assignee_ids: [] });
  const [canEdit, setCanEdit] = useState(false);
  const [errorModal, setErrorModal] = useState({ open: false, message: '', reasons: [] });
  const [successModal, setSuccessModal] = useState(false);
  const [linking, setLinking] = useState(false);
  const [selectedSurveyIds, setSelectedSurveyIds] = useState([]);
  // Per selected survey: { whole: bool, waveIds: [] }. whole=true (or a non-tracker)
  // means the survey is linked in its entirety; otherwise waveIds names the vagues.
  const [scopeBySurvey, setScopeBySurvey] = useState({});
  const [linkedWaveIds, setLinkedWaveIds] = useState(new Set()); // vagues taken by other slots
  const [wholeLinkedSurveyIds, setWholeLinkedSurveyIds] = useState(new Set()); // surveys linked whole elsewhere
  const [actionLoading, setActionLoading] = useState(false);
  const [projectAssignees, setProjectAssignees] = useState([]);
  const [projectAttestationEmail, setProjectAttestationEmail] = useState('');
  const [checklists, setChecklists] = useState([]);
  const [newCheckLabel, setNewCheckLabel] = useState('');
  const [addingCheck, setAddingCheck] = useState(false);
  const [unlinkConfirm, setUnlinkConfirm] = useState(null); // { linkId, label } | null

  // The link picker is a selection form: only Escape and the buttons dismiss it.
  const linkDismiss = useModalDismiss(() => resetPicker(), { backdropClose: false, enabled: linking });
  const errorDismiss = useModalDismiss(
    () => setErrorModal({ open: false, message: '', reasons: [] }), { enabled: errorModal.open });

  const fetchSlot = useCallback(async () => {
    try {
      setLoading(true);
      const [slotRes, slotsRes, usersRes, surveysRes, projectRes] = await Promise.all([
        api.get(`/projects/${projectId}/slots/${slotId}`),
        api.get(`/projects/${projectId}/slots`),
        api.get('/projects/assignable-users'),
        api.get('/projects/surveys-list'),
        api.get(`/projects/${projectId}`),
      ]);
      const s = slotRes.data.data;
      setSlot(s);
      setCanEdit(!!s.can_edit);
      setChecklists(s.checklists || []);
      setForm({
        name: s.name || '',
        wave_id: s.wave_id || null,
        assignee_ids: (s.assignees || []).map(a => a.id),
        livrables_deadline: toDateInput(s.livrables_deadline),
        reception_date: toDateInput(s.reception_date),
        facturation_date: toDateInput(s.facturation_date),
        attente_paiement_date: toDateInput(s.attente_paiement_date),
        payment_date: toDateInput(s.payment_date),
        attestation_email: s.attestation_email || '',
      });
      const data = slotsRes.data.data || {};
      setWaves((data.waves || []).map(w => ({ value: w.id, label: w.name })));
      setAssignableUsers(usersRes.data.data || []);

      // Filter out already-linked surveys
      const allSlots = [
        ...(data.waves || []).flatMap(w => w.survey_slots || []),
        ...(data.standalone_slots || []),
      ];
      // Everything linked by OTHER slots, read from the pivot rows.
      const otherLinks = allSlots
        .filter(sl => sl.id !== parseInt(slotId))
        .flatMap(sl => sl.linked_surveys || []);
      // A vague belongs to exactly one slot.
      setLinkedWaveIds(new Set(otherLinks.filter(l => l.survey_wave_id).map(l => l.survey_wave_id)));
      // A survey linked in its entirety blocks per-vague linking of that same survey.
      const wholeElsewhere = new Set(otherLinks.filter(l => !l.survey_wave_id).map(l => l.survey_id));
      setWholeLinkedSurveyIds(wholeElsewhere);
      // Tracker surveys stay selectable while they still have free vagues; a non-tracker
      // disappears from the list once it is linked anywhere.
      setSurveys((surveysRes.data.data || []).filter(sv => sv.is_tracker || !wholeElsewhere.has(sv.id)));
      setProjectAssignees(projectRes.data.data?.assignees || []);
      setProjectAttestationEmail(projectRes.data.data?.attestation_email || '');
    } catch {
      navigate(`/projects/${projectId}?tab=surveys`);
    } finally {
      setLoading(false);
    }
  }, [projectId, slotId, navigate]);

  useEffect(() => { fetchSlot(); }, [fetchSlot]);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/slots/${slotId}`, {
        name: form.name.trim(),
        wave_id: form.wave_id || null,
        assignee_ids: form.assignee_ids,
        livrables_deadline: form.livrables_deadline || null,
        reception_date: form.reception_date || null,
        facturation_date: form.facturation_date || null,
        attente_paiement_date: form.attente_paiement_date || null,
        payment_date: form.payment_date || null,
        attestation_email: form.attestation_email || null,
      });
      setSuccessModal(true);
    } catch (err) {
      setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur lors de la mise à jour' });
    } finally {
      setSaving(false);
    }
  };

  const resetPicker = () => {
    setLinking(false);
    setSelectedSurveyIds([]);
    setScopeBySurvey({});
  };

  const handleLink = async () => {
    const targets = selectedSurveyIds.map(id => surveys.find(s => s.id === id)).filter(Boolean);
    if (targets.length === 0) return;

    setActionLoading(true);
    const failures = [];
    try {
      // The endpoint takes one survey (plus its vagues) per call, so fan out. Partial
      // success is fine — whatever linked stays linked, the rest is reported.
      for (const sv of targets) {
        const scope = scopeBySurvey[sv.id] || { whole: false, waveIds: [] };
        try {
          // eslint-disable-next-line no-await-in-loop
          await api.put(`/projects/${projectId}/slots/${slotId}/link`, {
            survey_id: sv.id,
            // An empty array means "the whole survey".
            survey_wave_ids: sv.is_tracker && !scope.whole ? scope.waveIds : [],
          });
        } catch (err) {
          failures.push(`${sv.title} — ${err.response?.data?.message || 'erreur'}`);
        }
      }

      resetPicker();
      await fetchSlot();

      if (failures.length > 0) {
        setErrorModal({
          open: true,
          message: failures.length === targets.length
            ? 'Aucune enquête n\'a pu être liée'
            : 'Certaines enquêtes n\'ont pas pu être liées',
          reasons: failures,
        });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnlink = async () => {
    setActionLoading(true);
    try {
      // link_id removes one (survey, vague) row; omitting it clears the whole slot.
      await api.put(`/projects/${projectId}/slots/${slotId}/unlink`,
        unlinkConfirm?.linkId ? { link_id: unlinkConfirm.linkId } : {});
      setUnlinkConfirm(null);
      fetchSlot();
    } catch (err) {
      setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleChecklist = async (itemId) => {
    // Optimistic update
    setChecklists(prev => prev.map(c => c.id === itemId ? { ...c, is_completed: !c.is_completed } : c));
    try {
      await api.post(`/projects/${projectId}/checklists/${itemId}/toggle`);
      // Refresh to get completed_by info
      const res = await api.get(`/projects/${projectId}/slots/${slotId}`);
      setChecklists(res.data.data.checklists || []);
    } catch {
      // Revert on error
      setChecklists(prev => prev.map(c => c.id === itemId ? { ...c, is_completed: !c.is_completed } : c));
    }
  };

  const handleAddChecklist = async () => {
    if (!newCheckLabel.trim() || addingCheck || !slot) return;
    setAddingCheck(true);
    try {
      const res = await api.post(`/projects/${projectId}/checklists`, {
        step: slot.step || 'setup',
        label: newCheckLabel.trim(),
        survey_slot_id: parseInt(slotId),
      });
      setChecklists(prev => [...prev, res.data.data]);
      setNewCheckLabel('');
    } catch { }
    finally { setAddingCheck(false); }
  };

  const handleToggleFinished = async () => {
    setActionLoading(true);
    try {
      await api.post(`/projects/${projectId}/slots/${slotId}/toggle-finished`);
      fetchSlot();
    } catch (err) {
      const data = err.response?.data;
      setErrorModal({ open: true, message: data?.message || 'Erreur', reasons: data?.reasons || [] });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
      </div>
    );
  }

  if (!slot) return null;

  const links = slot.linked_surveys || [];
  const isLinked = links.length > 0;
  // The rich detail grid only makes sense for a single survey; with several we render a
  // compact row per link instead.
  // Date casts arrive as UTC ISO; parsing and re-formatting in the browser zone restores
  // the intended day. Never string-slice the ISO value — that shows the day before.
  const fmtDay = v => (v ? new Date(v).toLocaleDateString('fr-FR') : '—');
  const stepColor = STEP_COLORS[slot.step] || '#6b7280';
  // A slot can cover CATI and F2F surveys at once, so the header shows every type present.
  const linkTypes = [...new Set(
    (links.length > 0 ? links.map(l => l.survey) : [slot.survey])
      .filter(Boolean)
      .map(sv => (sv.poste_qcode === 'ref' ? 'F2F' : 'CATI'))
  )];

  return (
    <div style={{ padding: '20px 28px', maxWidth: 750, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>
            Modifier l'enquête
          </h5>
          <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 2 }}>{slot.name}</div>
        </div>
        {/* Terminé toggle */}
        {isLinked && canEdit && (
          <button onClick={handleToggleFinished} disabled={actionLoading}
            style={{
              padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
              border: slot.is_finished ? '1px solid #10b981' : '1px solid #d1d5db',
              background: slot.is_finished ? '#f0fdf4' : '#fff',
              color: slot.is_finished ? '#166534' : '#6b7280',
              opacity: actionLoading ? 0.5 : 1,
            }}>
            <FontAwesomeIcon icon={faFlagCheckered} size="sm" className="me-1" />
            {slot.is_finished ? 'Terminée' : 'Marquer terminée'}
          </button>
        )}
      </div>

      {/* Status bar */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 20px', marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: stepColor }} />
          <span style={{ fontSize: '0.72rem', fontWeight: 600, color: stepColor }}>{STEP_LABELS[slot.step] || slot.step}</span>
        </div>
        {isLinked ? (
          <span style={{ display: 'flex', gap: 4 }}>
            {linkTypes.map(t => (
              <span key={t} style={{
                fontSize: '0.62rem', fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                background: t === 'CATI' ? '#f3e8ff' : '#dbeafe', color: t === 'CATI' ? '#6b21a8' : '#1e40af',
              }}>
                {t}
              </span>
            ))}
          </span>
        ) : (
          <span style={{ fontSize: '0.72rem', color: '#a16207', display: 'flex', alignItems: 'center', gap: 4 }}>
            <FontAwesomeIcon icon={faClock} size="xs" /> Non liée
          </span>
        )}
        {slot.is_finished && (
          <span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#dcfce7', color: '#166534' }}>
            TERMINÉE
          </span>
        )}

        {/* Assignees — pushed to the right */}
        {(() => {
          const slotNames = (slot.assignees || []).map(a => a.name);
          const projectNames = projectAssignees.filter(a => !slotNames.includes(a.name)).map(a => a.name);
          if (slotNames.length === 0 && projectNames.length === 0) return null;
          return (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.68rem', color: '#6b7280' }}>
              <FontAwesomeIcon icon={faUser} size="xs" style={{ color: '#d1d5db' }} />
              {slotNames.length > 0 && (
                <span>{slotNames.join(', ')}</span>
              )}
              {projectNames.length > 0 && (
                <span style={{ color: '#9ca3af' }}>
                  {slotNames.length > 0 && '· '}
                  <span style={{ fontStyle: 'italic' }}>{projectNames.join(', ')}</span>
                  <span style={{ fontSize: '0.58rem', marginLeft: 3 }}>(projet)</span>
                </span>
              )}
            </div>
          );
        })()}
      </div>

      {/* Slot form */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px', marginBottom: 16 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 16 }}>
          Informations de l'enquête
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Name */}
          <div>
            <label style={labelStyle}>Nom *</label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              disabled={!canEdit}
              style={{ ...inputStyle, background: canEdit ? '#fff' : '#f9fafb', color: canEdit ? '#111827' : '#9ca3af' }}
              onFocus={canEdit ? focusInput : undefined} onBlur={canEdit ? blurInput : undefined} />
          </div>

          {/* Wave */}
          {waves.length > 0 && (
            <div>
              <label style={labelStyle}>Groupe</label>
              <select value={form.wave_id || ''} onChange={e => setForm(f => ({ ...f, wave_id: e.target.value ? parseInt(e.target.value) : null }))}
                disabled={!canEdit}
                style={{ ...inputStyle, cursor: canEdit ? 'pointer' : 'default', background: canEdit ? '#fff' : '#f9fafb', color: canEdit ? '#374151' : '#9ca3af' }}>
                <option value="">Sans groupe</option>
                {waves.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
            </div>
          )}

          {/* Assignees */}
          {canEdit ? (
            <div>
              <label style={labelStyle}>Assignés</label>
              <SearchableMultiSelect
                options={assignableUsers}
                selectedValues={form.assignee_ids}
                onChange={ids => setForm(f => ({ ...f, assignee_ids: ids }))}
                placeholder="Ajouter des personnes..."
                getOptionLabel={u => u.name}
                getOptionMeta={u => u.role_display || u.role}
              />
            </div>
          ) : (
            <div>
              <label style={labelStyle}>Assignés</label>
              <div style={{ fontSize: '0.82rem', color: '#6b7280', padding: '10px 14px', background: '#f9fafb', borderRadius: 8 }}>
                {form.assignee_ids.length > 0
                  ? assignableUsers.filter(u => form.assignee_ids.includes(u.id)).map(u => u.name).join(', ')
                  : 'Aucun'}
              </div>
            </div>
          )}

          {/* Dates closing */}
          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #f3f4f6', paddingTop: 14, marginTop: 4 }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>Dates closing</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                ['livrables_deadline', 'Échéance livrables'],
                ['reception_date', 'Date réception'],
                ['facturation_date', 'Date facturation'],
                ['attente_paiement_date', 'Date mise en attente de paiement'],
                ['payment_date', 'Date de paiement (encaissé)'],
              ].map(([key, label]) => {
                // La date de facturation est posée automatiquement en cochant « Facturation » ;
                // elle ne se corrige qu'ensuite, en attente de paiement ou une fois encaissée.
                const locked = key === 'facturation_date'
                  && !['attente_paiement', 'encaisse'].includes(slot?.closing_step);
                const editable = canEdit && !locked;
                return (
                  <div key={key}>
                    <label style={labelStyle}>{label}</label>
                    <input type="date" value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      disabled={!editable}
                      style={{ ...inputStyle, background: editable ? '#fff' : '#f9fafb', color: editable ? '#111827' : '#9ca3af' }}
                      onFocus={editable ? focusInput : undefined} onBlur={editable ? blurInput : undefined} />
                    {locked && canEdit && (
                      <div style={{ fontSize: '0.66rem', color: '#9ca3af', marginTop: 4 }}>
                        Modifiable une fois en attente de paiement ou encaissée.
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Email attestation de référence</label>
                <input type="email" value={form.attestation_email} onChange={e => setForm(f => ({ ...f, attestation_email: e.target.value }))}
                  disabled={!canEdit}
                  placeholder={projectAttestationEmail ? `Par défaut : ${projectAttestationEmail}` : 'contact@client.com'}
                  style={{ ...inputStyle, background: canEdit ? '#fff' : '#f9fafb', color: canEdit ? '#111827' : '#9ca3af' }}
                  onFocus={canEdit ? focusInput : undefined} onBlur={canEdit ? blurInput : undefined} />
                <div style={{ fontSize: '0.66rem', color: '#9ca3af', marginTop: 4 }}>
                  {form.attestation_email
                    ? 'Cet email surcharge celui du projet pour cette enquête.'
                    : projectAttestationEmail
                      ? `Hérité du projet : ${projectAttestationEmail}`
                      : 'Aucun email défini sur le projet — saisir un email ici ou sur le projet.'}
                </div>
              </div>
            </div>

            {/* Délai de relance paiement — enregistré immédiatement, pas au Save du formulaire */}
            {slot.step === 'closing'
              && ['facturation', 'attente_paiement'].includes(slot.closing_step)
              && slot.payment_relance && (
              <div style={{ marginTop: 14 }}>
                <PaymentRelanceCard
                  projectId={projectId}
                  slotId={slot.id}
                  state={slot.payment_relance}
                  canEdit={!!slot.can_set_payment_relance}
                  onSaved={() => fetchSlot()}
                />
              </div>
            )}
          </div>
        </div>

        {/* Save */}
        {canEdit && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 18, marginTop: 18, borderTop: '1px solid #f3f4f6' }}>
            <button onClick={() => navigate(`/projects/${projectId}?tab=surveys`)}
              style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer' }}>
              Annuler
            </button>
            <button onClick={handleSave} disabled={saving || !form.name.trim()}
              style={{
                padding: '9px 20px', borderRadius: 8, border: 'none',
                background: form.name.trim() ? '#dc2626' : '#e5e7eb',
                color: form.name.trim() ? '#fff' : '#9ca3af',
                fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.5 : 1,
              }}>
              <FontAwesomeIcon icon={faSave} size="sm" className="me-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        )}
      </div>

      {/* Linked survey section */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {links.length > 1 ? `Enquêtes liées (${links.length})` : 'Enquête liée'}
          </div>
          {isLinked && !slot.is_finished && canEdit && (
            <div style={{ display: 'flex', gap: 8 }}>
              {/* A slot can cover several surveys, so linking stays available once one
                  is already attached. */}
              <button onClick={() => { resetPicker(); setLinking(true); }}
                style={{ fontSize: '0.72rem', padding: '4px 12px', borderRadius: 6, border: '1px solid #dc2626', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faLink} size="xs" className="me-1" />Ajouter
              </button>
              {links.length === 1 && (
                <button onClick={() => setUnlinkConfirm({ linkId: links[0].id, label: links[0].survey?.title })}
                  style={{ fontSize: '0.72rem', padding: '4px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>
                  <FontAwesomeIcon icon={faUnlink} size="xs" className="me-1" />Délier
                </button>
              )}
            </div>
          )}
        </div>

        {isLinked ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Full detail block per linked survey — same layout whether the slot covers
                one survey or several. */}
            {links.map((l, idx) => {
              const sv = l.survey || {};
              const cati = sv.poste_qcode !== 'ref';
              const wv = l.survey_wave;
              const waveName = wv ? (wv.label || `Vague ${(wv.order_index ?? 0) + 1}`) : null;
              return (
                <div key={l.id} style={{
                  display: 'flex', flexDirection: 'column', gap: 12,
                  paddingTop: idx === 0 ? 0 : 20,
                  borderTop: idx === 0 ? 'none' : '1px solid #f3f4f6',
                }}>
                  {/* Survey identity */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#f9fafb', borderRadius: 8 }}>
                    <FontAwesomeIcon icon={faLink} style={{ color: '#9ca3af' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {sv.title}
                        <VagueBadge wave={wv} style={{ fontSize: '0.6rem', padding: '2px 7px' }} />
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>LimeSurvey ID: {sv.limesurvey_id}</div>
                    </div>
                    <span style={{
                      fontSize: '0.62rem', fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                      background: cati ? '#f3e8ff' : '#dbeafe', color: cati ? '#6b21a8' : '#1e40af',
                    }}>
                      {cati ? 'CATI' : 'F2F'}
                    </span>
                    {!slot.is_finished && canEdit && (
                      <button
                        onClick={() => setUnlinkConfirm({ linkId: l.id, label: wv ? `${sv.title} — ${waveName}` : sv.title })}
                        title="Délier"
                        style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', padding: 2 }}>
                        <FontAwesomeIcon icon={faUnlink} size="sm" />
                      </button>
                    )}
                  </div>

                  {/* Survey details grid — adapts per type */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <DetailField label="Date début" value={fmtDay(sv.date_debut)} />
                    <DetailField label="Date fin" value={fmtDay(sv.date_fin)} />

                    {/* Vague-scoped link: its window follows the survey's, and its
                        objective sits beside the global one — each pair on its own row. */}
                    {wv && <DetailField label={`Début ${waveName}`} value={fmtDay(wv.date_debut)} />}
                    {wv && <DetailField label={`Fin ${waveName}`} value={fmtDay(wv.date_fin)} />}

                    <DetailField label="Target global" value={sv.general_target || '—'} />
                    {wv && <DetailField label={`Target ${waveName}`} value={wv.target ?? '—'} />}

                    <DetailField label="Contrôle (%)" value={sv.control_percentage ? `${sv.control_percentage}%` : '—'} />

                    {/* F2F fields */}
                    {!cati && (
                      <>
                        <DetailField label="Prix / complété" value={sv.price_per_complete ? `${Number(sv.price_per_complete).toLocaleString('fr-FR')} MAD` : '—'} />
                        <DetailField label="Frais de déplacement" value={sv.frais_deplacement ? `${Number(sv.frais_deplacement).toLocaleString('fr-FR')} MAD` : '—'} />
                        <DetailField label="Marge imprévus" value={sv.marge_imprevus ? `${Number(sv.marge_imprevus).toLocaleString('fr-FR')} MAD` : '—'} />
                      </>
                    )}

                    {/* CATI per_complete fields */}
                    {cati && sv.payment_type === 'per_complete' && (
                      <DetailField label="Prix / complété" value={sv.price_per_complete ? `${Number(sv.price_per_complete).toLocaleString('fr-FR')} MAD` : '—'} />
                    )}

                    {/* CATI per_day fields */}
                    {cati && sv.payment_type === 'per_day' && (
                      <>
                        <DetailField label="Prix / jour" value={sv.price_per_day ? `${Number(sv.price_per_day).toLocaleString('fr-FR')} MAD` : '—'} />
                        <DetailField label="Jours prévus" value={sv.target_days || '—'} />
                        <DetailField label="Enquêteurs prévus" value={sv.target_interviewers || '—'} />
                      </>
                    )}
                  </div>

                  {/* Edit survey link — not for resp_admin */}
                  {user?.role !== 'resp_admin' && sv.id && (
                    <div style={{ paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
                      <button onClick={() => navigate(`/cati/surveys/${sv.id}`)}
                        style={{
                          fontSize: '0.78rem', fontWeight: 500, padding: '8px 16px', borderRadius: 8,
                          border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer',
                          width: '100%', textAlign: 'center',
                        }}>
                        Modifier les détails de l'enquête
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '0.82rem', color: '#d1d5db', marginBottom: 12 }}>Aucune enquête liée</div>
            {canEdit && (
              <button onClick={() => { resetPicker(); setLinking(true); }}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: '1px solid #dc2626',
                  background: '#fff', color: '#dc2626', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
                }}>
                <FontAwesomeIcon icon={faLink} size="sm" className="me-2" />Lier une enquête
              </button>
            )}
          </div>
        )}
      </div>

      {/* Checklists */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px', marginTop: 16 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12 }}>
          Checklist{checklists.length > 0 ? ` · ${checklists.filter(c => c.is_completed).length}/${checklists.length}` : ''}
        </div>
        {(() => {
          const byStep = {};
          checklists.forEach(item => { const s = item.step || 'autre'; if (!byStep[s]) byStep[s] = []; byStep[s].push(item); });
          const order = ['ao', 'setup', 'terrain', 'reporting', 'closing', 'autre'];
          return Object.keys(byStep).sort((a, b) => order.indexOf(a) - order.indexOf(b)).map(step => {
            const items = byStep[step];
            const accent = STEP_COLORS[step] || '#9e9e9e';
            const stepDone = items.filter(i => i.is_completed).length;
            return (
              <div key={step} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, paddingBottom: 4, borderBottom: `1.5px solid ${accent}20` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0 }} />
                    <span style={{ fontSize: '0.62rem', fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{STEP_LABELS[step] || step}</span>
                  </div>
                  <span style={{ fontSize: '0.58rem', fontWeight: 600, color: '#bdbdbd' }}>{stepDone}/{items.length}</span>
                </div>
                {items.map(item => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f9fafb' }}>
                    <div
                      onClick={() => canEdit && handleToggleChecklist(item.id)}
                      style={{
                        width: 16, height: 16, borderRadius: 3, flexShrink: 0,
                        cursor: canEdit ? 'pointer' : 'default',
                        border: item.is_completed ? 'none' : '2px solid #d1d5db',
                        background: item.is_completed ? '#dc2626' : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.15s',
                      }}>
                      {item.is_completed && <svg width="8" height="6" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        fontSize: '0.78rem', color: item.is_completed ? '#9ca3af' : '#111827',
                        textDecoration: item.is_completed ? 'line-through' : 'none',
                        fontWeight: item.is_completed ? 400 : 500,
                      }}>
                        {item.label}
                      </span>
                      {item.is_completed && item.completed_by && (
                        <div style={{ fontSize: '0.62rem', color: '#d1d5db', marginTop: 1 }}>
                          {item.completed_by.name} {item.completed_at ? `· ${new Date(item.completed_at).toLocaleDateString('fr-FR')}` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          });
        })()}
        {checklists.length === 0 && (
          <div style={{ fontSize: '0.78rem', color: '#d1d5db', padding: '8px 0' }}>Aucun élément</div>
        )}
        {/* Add input */}
        {canEdit && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
            <input
              type="text"
              value={newCheckLabel}
              onChange={e => setNewCheckLabel(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddChecklist(); }}
              placeholder="Ajouter un élément..."
              style={{ flex: 1, fontSize: '0.75rem', padding: '5px 10px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none' }}
            />
            <button
              onClick={handleAddChecklist}
              disabled={!newCheckLabel.trim() || addingCheck}
              style={{
                padding: '4px 10px', borderRadius: 6, border: 'none',
                background: newCheckLabel.trim() ? '#dc2626' : '#e5e7eb',
                color: newCheckLabel.trim() ? '#fff' : '#9ca3af',
                cursor: 'pointer', fontSize: '0.72rem',
              }}>
              +
            </button>
          </div>
        )}
      </div>

      {/* Link survey modal */}
      {linking && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div {...linkDismiss} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
          <div style={{
            position: 'relative', background: '#fff', borderRadius: 12, padding: '28px 32px',
            width: 560, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            animation: 'linkModalIn 0.15s ease',
          }}>
            <h5 style={{ margin: '0 0 6px', fontSize: '0.95rem', fontWeight: 700, color: '#111827' }}>
              <FontAwesomeIcon icon={faLink} className="me-2" style={{ color: '#dc2626' }} />
              Lier des enquêtes
            </h5>
            <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: '0 0 18px' }}>
              Sélectionnez une ou plusieurs enquêtes à associer à « {slot?.name} »
            </p>

            <div style={{ marginBottom: 18 }}>
              <SearchableMultiSelect
                options={surveys}
                selectedValues={selectedSurveyIds}
                onChange={ids => {
                  setSelectedSurveyIds(ids);
                  // Drop scope entries for surveys that were just deselected.
                  setScopeBySurvey(prev => {
                    const next = {};
                    ids.forEach(id => { next[id] = prev[id] || { whole: false, waveIds: [] }; });
                    return next;
                  });
                }}
                placeholder="Rechercher par titre ou ID LimeSurvey..."
                getOptionLabel={s => s.title}
                getOptionMeta={s => `LS: ${s.limesurvey_id}`}
              />
            </div>

            {/* One block per selected survey. Trackers additionally choose their scope:
                specific vagues, or the survey as a whole — never both, matching the
                server-side rule. */}
            <div style={{ maxHeight: '45vh', overflowY: 'auto', marginBottom: 18 }}>
              {selectedSurveyIds.map(id => {
                const sv = surveys.find(s => s.id === id);
                if (!sv) return null;
                const scope = scopeBySurvey[id] || { whole: false, waveIds: [] };
                const waves = sv.waves || [];
                // Date casts arrive as UTC ISO, so midnight local is 23:00 the day before; parsing
              // and re-formatting in the browser zone restores the intended day. Slicing the
              // ISO string instead would show the previous day.
              const fmt = d => (d ? new Date(d).toLocaleDateString('fr-FR') : '?');
                const wholeBlocked = waves.some(w => linkedWaveIds.has(w.id));
                const vaguesBlocked = wholeLinkedSurveyIds.has(sv.id);
                const setScope = patch => setScopeBySurvey(prev => ({ ...prev, [id]: { ...scope, ...patch } }));
                const toggleWave = wid => setScope({
                  waveIds: scope.waveIds.includes(wid)
                    ? scope.waveIds.filter(x => x !== wid)
                    : [...scope.waveIds, wid],
                });
                const modeBtn = (active, disabled) => ({
                  flex: 1, padding: '6px 10px', borderRadius: 7, fontSize: '0.72rem', fontWeight: 600,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  border: `1px solid ${active ? '#dc2626' : '#d1d5db'}`,
                  background: active ? '#fef2f2' : '#fff',
                  color: disabled ? '#d1d5db' : (active ? '#dc2626' : '#6b7280'),
                });
                const incomplete = sv.is_tracker && !scope.whole && scope.waveIds.length === 0;

                return (
                  <div key={id} style={{
                    border: `1px solid ${incomplete ? '#d1d5db' : '#e5e7eb'}`,
                    borderRadius: 9, padding: '12px 14px', marginBottom: 10,
                    background: incomplete ? '#f9fafb' : '#fff',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: sv.is_tracker ? 10 : 0 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827' }}>{sv.title}</div>
                        <div style={{ fontSize: '0.67rem', color: '#9ca3af' }}>LS: {sv.limesurvey_id}</div>
                      </div>
                      <span style={{
                        fontSize: '0.58rem', fontWeight: 600, padding: '2px 7px', borderRadius: 8,
                        background: sv.poste_qcode === 'ref' ? '#dbeafe' : '#f3e8ff',
                        color: sv.poste_qcode === 'ref' ? '#1e40af' : '#6b21a8',
                      }}>
                        {sv.poste_qcode === 'ref' ? 'F2F' : 'CATI'}
                      </span>
                    </div>

                    {sv.is_tracker && (
                      <>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                          <button type="button" disabled={vaguesBlocked}
                            onClick={() => setScope({ whole: false })}
                            style={modeBtn(!scope.whole, vaguesBlocked)}>
                            Vagues précises
                          </button>
                          <button type="button" disabled={wholeBlocked}
                            onClick={() => setScope({ whole: true, waveIds: [] })}
                            style={modeBtn(scope.whole, wholeBlocked)}>
                            Toute l'enquête
                          </button>
                        </div>

                        {(wholeBlocked || vaguesBlocked) && (
                          <div style={{ fontSize: '0.68rem', color: '#6b7280', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 9px', marginBottom: 8 }}>
                            {vaguesBlocked
                              ? "Déjà liée en entier ailleurs : impossible d'y ajouter des vagues."
                              : "Certaines vagues sont déjà liées : impossible de lier l'enquête entière."}
                          </div>
                        )}

                        {!scope.whole ? (
                          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, maxHeight: 180, overflowY: 'auto' }}>
                            {waves.map((w, i) => {
                              const taken = linkedWaveIds.has(w.id);
                              const disabled = taken || vaguesBlocked;
                              const checked = scope.waveIds.includes(w.id);
                              return (
                                <label key={w.id} style={{
                                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 11px',
                                  borderTop: i === 0 ? 'none' : '1px solid #f3f4f6',
                                  cursor: disabled ? 'not-allowed' : 'pointer',
                                  background: checked ? '#fef2f2' : '#fff', opacity: disabled ? 0.55 : 1,
                                }}>
                                  <input type="checkbox" checked={checked} disabled={disabled}
                                    onChange={() => toggleWave(w.id)} style={{ accentColor: '#dc2626' }} />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#111827' }}>
                                      {w.label || `Vague #${w.id}`}
                                      {taken && <span style={{ color: '#9ca3af', fontWeight: 400 }}> — déjà liée</span>}
                                    </div>
                                    <div style={{ fontSize: '0.66rem', color: '#9ca3af' }}>
                                      {fmt(w.date_debut)} → {fmt(w.date_fin)}
                                      {w.target ? ` · cible ${w.target}` : ''}
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                            {waves.length === 0 && (
                              <div style={{ fontSize: '0.7rem', color: '#dc2626', padding: '9px 11px' }}>
                                Aucune vague définie pour cette enquête.
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ fontSize: '0.72rem', color: '#6b7280', background: '#f9fafb', borderRadius: 8, padding: '9px 11px' }}>
                            Liée en entier : ses {waves.length} vague(s) sont couvertes.
                          </div>
                        )}

                        {incomplete && (
                          <div style={{ fontSize: '0.68rem', color: '#6b7280', marginTop: 6 }}>
                            Choisissez au moins une vague, ou « Toute l'enquête ».
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => resetPicker()}
                style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
                Annuler
              </button>
              {(() => {
                // Every selected tracker must have resolved its scope before linking.
                const canLink = selectedSurveyIds.length > 0 && selectedSurveyIds.every(id => {
                  const sv = surveys.find(s => s.id === id);
                  if (!sv?.is_tracker) return true;
                  const sc = scopeBySurvey[id] || { whole: false, waveIds: [] };
                  return sc.whole || sc.waveIds.length > 0;
                });
                return (
                  <button onClick={handleLink} disabled={!canLink || actionLoading}
                    style={{
                      padding: '8px 20px', borderRadius: 6, border: 'none',
                      background: canLink ? '#dc2626' : '#e5e7eb',
                      color: canLink ? '#fff' : '#9ca3af',
                      fontSize: '0.78rem', fontWeight: 600, cursor: canLink ? 'pointer' : 'not-allowed',
                      opacity: actionLoading ? 0.5 : 1,
                    }}>
                    {actionLoading
                      ? 'En cours...'
                      : `Lier${selectedSurveyIds.length > 1 ? ` (${selectedSurveyIds.length})` : ''}`}
                  </button>
                );
              })()}
            </div>
          </div>
          <style>{`@keyframes linkModalIn { from { opacity:0; transform:scale(0.95) translateY(-4px); } to { opacity:1; transform:scale(1) translateY(0); } }`}</style>
        </div>
      )}

      {/* Modals */}
      <ConfirmModal
        isOpen={successModal}
        onClose={() => { setSuccessModal(false); navigate(`/projects/${projectId}?tab=surveys`); }}
        title="Modifications enregistrées"
        message="L'enquête a été mise à jour avec succès."
        variant="success"
      />
      <ConfirmModal
        isOpen={!!unlinkConfirm}
        onClose={() => setUnlinkConfirm(null)}
        onConfirm={handleUnlink}
        title="Délier l'enquête ?"
        message={`${unlinkConfirm?.label ? `« ${unlinkConfirm.label} »` : 'L\'enquête'} sera dissocié(e) de cet emplacement. Le lien pourra être recréé ultérieurement.`}
        variant="warning"
        confirmLabel="Délier"
        loading={actionLoading}
      />
      {errorModal.open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div {...errorDismiss} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
          <div style={{ position: 'relative', background: '#fff', borderRadius: 12, padding: '28px 32px', width: 420, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#dc2626', fontSize: '0.9rem' }} />
              </div>
              <h5 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#111827' }}>{errorModal.message}</h5>
            </div>
            {errorModal.reasons?.length > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                {errorModal.reasons.map((r, i) => (
                  <div key={i} style={{ fontSize: '0.78rem', color: '#991b1b', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} size="xs" />{r}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setErrorModal({ open: false, message: '', reasons: [] })}
                style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* Shared styles */
const labelStyle = { fontSize: '0.72rem', fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };
const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: '0.85rem', outline: 'none', background: '#fff', color: '#111827' };
const focusInput = e => { e.target.style.borderColor = '#dc2626'; };
const blurInput = e => { e.target.style.borderColor = '#e5e7eb'; };

const DetailField = ({ label, value }) => (
  <div style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: 6 }}>
    <div style={{ fontSize: '0.62rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: '0.82rem', fontWeight: 500, color: '#111827' }}>{value}</div>
  </div>
);

export default SlotEdit;
